import { useState } from 'react'
import { addDoc, updateDoc, deleteDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useAuth } from 'contexts/AuthContext'
import { InventoryList, UserInventoryList } from '@otw/models'
import { db } from '@otw/models/db'

interface UseAdminListBuilderOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

interface CreateListParams {
  name: string
  description: string
  initialInventoryIds: string[]
  associatedUserIds?: string[]
}

export const useAdminListBuilder = (options?: UseAdminListBuilderOptions) => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()
  const [loading, setLoading] = useState(false)

  // Fetch all lists (admin view)
  const {
    data: lists,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminInventoryLists'],
    queryFn: async () => {
      if (userInfo?.role !== 'admin' && userInfo?.role !== 'babyAdmin') {
        throw new Error('Unauthorized access')
      }

      const snapshot = await getDocs(db.inventoryLists)
      const listDocs = snapshot.docs.map(d => d.data())

      // Associations live at top-level userInventoryLists (not a nested path).
      const associationsMap = new Map<string, string[]>()
      try {
        const userAssociations = await getDocs(db.userInventoryLists)
        userAssociations.docs.forEach(associationDoc => {
          const data = associationDoc.data() as {
            inventoryListId?: string
            userId?: string
          }
          if (!data.inventoryListId || !data.userId) return
          if (!associationsMap.has(data.inventoryListId)) {
            associationsMap.set(data.inventoryListId, [])
          }
          associationsMap.get(data.inventoryListId)?.push(data.userId)
        })
      } catch (assocErr) {
        console.error('Error loading list associations:', assocErr)
      }

      return listDocs.map(list => ({
        ...list,
        associatedUserIds: associationsMap.get(list.id) || [],
      }))
    },
    enabled: userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin',
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Create a new list (with optional user association)
  const createList = useMutation({
    mutationFn: async ({ name, description, initialInventoryIds, associatedUserIds = [] }: CreateListParams) => {
      if (userInfo?.role !== 'admin' && userInfo?.role !== 'babyAdmin') {
        throw new Error('Unauthorized access')
      }

      const listPayload: InventoryList = {
        id: '',
        dateCreated: new Date().toISOString(),
        createdBy: userInfo.id,
        inventoryIds: initialInventoryIds,
        name,
        description,
        isArchived: false,
      }
      const listDoc = await addDoc(db.inventoryLists, listPayload)

      await Promise.all(
        associatedUserIds.map(userId => {
          const associationPayload: UserInventoryList = {
            id: '',
            userId,
            inventoryListId: listDoc.id,
            dateAdded: new Date().toISOString(),
          }
          return addDoc(db.userInventoryLists, associationPayload)
        }),
      )

      return listDoc.id
    },
    onSuccess: () => {
      showSuccess('List created successfully')
      queryClient.invalidateQueries({ queryKey: ['adminInventoryLists'] })
      options?.onSuccess?.()
    },
    onError: (err: Error) => {
      showError('Error creating list')
      console.error('Error creating list:', err)
      options?.onError?.(err)
    },
  })

  // Add/Update user associations
  const updateListAssociations = useMutation({
    mutationFn: async ({ listId, userIds }: { listId: string; userIds: string[] }) => {
      const currentAssociationsQuery = query(db.userInventoryLists, where('inventoryListId', '==', listId))
      const currentAssociations = await getDocs(currentAssociationsQuery)

      await Promise.all(currentAssociations.docs.map(associationDoc => deleteDoc(associationDoc.ref)))

      await Promise.all(
        userIds.map(userId => {
          const associationPayload: UserInventoryList = {
            id: '',
            userId,
            inventoryListId: listId,
            dateAdded: new Date().toISOString(),
          }
          return addDoc(db.userInventoryLists, associationPayload)
        }),
      )
    },
    onSuccess: () => {
      showSuccess('List associations updated successfully')
      queryClient.invalidateQueries({ queryKey: ['adminInventoryLists'] })
      options?.onSuccess?.()
    },
    onError: (err: Error) => {
      showError('Error updating list associations')
      console.error('Error updating list associations:', err)
      options?.onError?.(err)
    },
  })

  const addVehiclesToList = useMutation({
    mutationFn: async ({ listId, vehicleIds }: { listId: string; vehicleIds: string[] }) => {
      const listRef = doc(db.inventoryLists, listId)
      const listDoc = await getDoc(listRef)
      const currentList = listDoc.data() as InventoryList

      const updatedIds = [...new Set([...currentList.inventoryIds, ...vehicleIds])]
      await updateDoc(listRef, { inventoryIds: updatedIds })
    },
    onSuccess: () => {
      showSuccess('Vehicles added to list successfully')
      queryClient.invalidateQueries({ queryKey: ['adminInventoryLists'] })
      options?.onSuccess?.()
    },
    onError: (err: Error) => {
      showError('Error adding vehicles to list')
      console.error('Error adding vehicles to list:', err)
      options?.onError?.(err)
    },
  })

  const deleteList = useMutation({
    mutationFn: async (listId: string) => {
      await deleteDoc(doc(db.inventoryLists, listId))

      const userAssociationsQuery = query(db.userInventoryLists, where('inventoryListId', '==', listId))
      const userAssociations = await getDocs(userAssociationsQuery)
      await Promise.all(userAssociations.docs.map(associationDoc => deleteDoc(associationDoc.ref)))
    },
    onSuccess: () => {
      showSuccess('List deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['adminInventoryLists'] })
      options?.onSuccess?.()
    },
    onError: (err: Error) => {
      showError('Error deleting list')
      console.error('Error deleting list:', err)
      options?.onError?.(err)
    },
  })

  return {
    lists: lists ?? [],
    loading: isLoading || loading,
    isError,
    error,
    createList: createList.mutate,
    updateListAssociations: updateListAssociations.mutate,
    addVehiclesToList: addVehiclesToList.mutate,
    deleteList: deleteList.mutate,
    refetchLists: refetch,
  }
}
